import R from 'ramda'
import Sjcl from '@cryptolize/sjcl'

/**
 * Encrypt bitArray
 *
 * @param {bitArray} data
 * @param {Base64UrlString} key
 * @param {Object} $2 encryption params
 * @param {Base64UrlString} $2.iv initialization vector
 * @param {String} $2.mode aes encryption mode ['ccm', 'gcm', 'ocb2']
 * @param {Number} $2.ts tag size [64, 96, 128]
 * @param {String} $2.adata authenticated data to associate with the data
 * @return {bitArray} the encrypted data
 * @throws {Error} if a parameter is invalid
 * @private
 */
function _encrypt (data, key, { iv, mode, ts, adata }) {
  return Sjcl.mode[mode].encrypt(
    new Sjcl.cipher.aes(Sjcl.codec.base64url.toBits(key)), // eslint-disable-line new-cap
    data,
    Sjcl.codec.base64url.toBits(iv),
    Sjcl.codec.utf8String.toBits(adata),
    ts
  )
}

/**
 * Decrypt bitArray
 *
 * @param {bitArray} data
 * @param {Base64UrlString} key
 * @param {Object} $2 encryption params
 * @param {Base64UrlString} $2.iv initialization vector
 * @param {String} $2.mode aes encryption mode ['ccm', 'gcm', 'ocb2']
 * @param {Number} $2.ts tag size [64, 96, 128]
 * @param {String} $2.adata authenticated data to associate with the data
 * @return {bitArray} the decrypted data
 * @throws {Error} if a parameter is invalid, data is corrupt or wrong key
 * @private
 */
function _decrypt (data, key, { iv, mode, ts, adata }) {
  return Sjcl.mode[mode].decrypt(
    new Sjcl.cipher.aes(Sjcl.codec.base64url.toBits(key)), // eslint-disable-line new-cap
    data,
    Sjcl.codec.base64url.toBits(iv),
    Sjcl.codec.utf8String.toBits(adata),
    ts
  )
}

/**
 * Encrypt text
 *
 * @param {String} data
 * @param {Base64UrlString} key
 * @param {Object} encryptionParams
 * @param {Base64UrlString} encryptionParams.iv initialization vector
 * @param {String} encryptionParams.mode aes encryption mode ['ccm', 'gcm', 'ocb2']
 * @param {Number} encryptionParams.ts tag size [64, 96, 128]
 * @param {String} encryptionParams.adata authenticated data to associate with the data
 * @return {Base64UrlString} the encrypted data
 * @throws {Error} if a parameter is invalid
 * @public
 */
export function encryptText (data, key, encryptionParams) {
  return Sjcl.codec.base64url.fromBits(_encrypt(Sjcl.codec.utf8String.toBits(data), key, encryptionParams))
}

/**
 * Encrypt object
 *
 * @param {Object} data
 * @param {Base64UrlString} key
 * @param {Object} encryptionParams
 * @param {Base64UrlString} encryptionParams.iv initialization vector
 * @param {String} encryptionParams.mode aes encryption mode ['ccm', 'gcm', 'ocb2']
 * @param {Number} encryptionParams.ts tag size [64, 96, 128]
 * @param {String} encryptionParams.adata authenticated data to associate with the data
 * @return {Base64UrlString} the encrypted data
 * @throws {Error} if a parameter is invalid
 * @public
 */
export function encryptObject (data, key, encryptionParams) {
  return encryptText(JSON.stringify(data), key, encryptionParams)
}

/**
 * Encrypt file
 *
 * @param {ArrayBuffer} data
 * @param {Base64UrlString} key
 * @param {Object} encryptionParams
 * @param {Base64UrlString} encryptionParams.iv initialization vector
 * @param {String} encryptionParams.mode aes encryption mode ['ccm', 'gcm', 'ocb2']
 * @param {Number} encryptionParams.ts tag size [64, 96, 128]
 * @param {String} encryptionParams.adata authenticated data to associate with the data
 * @return {ArrayBuffer} the encrypted data
 * @throws {Error} if a parameter is invalid
 * @private
 */
export function encryptFile (data, key, encryptionParams) {
  return R.compose(
    R.prop('buffer'),
    R.constructN(1, Uint8Array),
    Sjcl.codec.bytes.fromBits,
    R.curry(_encrypt)(R.__, key, encryptionParams),
    Sjcl.codec.bytes.toBits,
    R.constructN(1, Uint8Array)
  )(data)
}

/**
 * Encrypt key
 *
 * @param {Base64UrlString} data
 * @param {Base64UrlString} key
 * @param {Object} encryptionParams
 * @param {Base64UrlString} encryptionParams.iv initialization vector
 * @param {String} encryptionParams.mode aes encryption mode ['ccm', 'gcm', 'ocb2']
 * @param {Number} encryptionParams.ts tag size [64, 96, 128]
 * @param {String} encryptionParams.adata authenticated data to associate with the data
 * @return {Base64UrlString} the encrypted data
 * @throws {Error} if a parameter is invalid
 * @public
 */
export function encryptKey (data, key, encryptionParams) {
  return Sjcl.codec.base64url.fromBits(_encrypt(Sjcl.codec.base64url.toBits(data), key, encryptionParams))
}

/**
 * Decrypt text
 *
 * @param {Base64UrlString} data
 * @param {Base64UrlString} key
 * @param {Object} encryptionParams
 * @param {Base64UrlString} encryptionParams.iv initialization vector
 * @param {String} encryptionParams.mode aes encryption mode ['ccm', 'gcm', 'ocb2']
 * @param {Number} encryptionParams.ts tag size [64, 96, 128]
 * @param {String} encryptionParams.adata authenticated data to associate with the data
 * @return {String} the decrypted data
 * @throws {Error} if a parameter is invalid, data is corrupt or wrong key
 * @public
 */
export function decryptText (data, key, encryptionParams) {
  return Sjcl.codec.utf8String.fromBits(_decrypt(Sjcl.codec.base64url.toBits(data), key, encryptionParams))
}

/**
 * Decrypt object
 *
 * @param {Base64UrlString} data
 * @param {Base64UrlString} key
 * @param {Object} encryptionParams
 * @param {Base64UrlString} encryptionParams.iv initialization vector
 * @param {String} encryptionParams.mode aes encryption mode ['ccm', 'gcm', 'ocb2']
 * @param {Number} encryptionParams.ts tag size [64, 96, 128]
 * @param {String} encryptionParams.adata authenticated data to associate with the data
 * @return {Object} the decrypted data
 * @throws {Error} if a parameter is invalid, data is corrupt or wrong key
 * @public
 */
export function decryptObject (data, key, encryptionParams) {
  return JSON.parse(decryptText(data, key, encryptionParams))
}

/**
 * Decrypt file
 *
 * @param {ArrayBuffer} data
 * @param {Base64UrlString} key
 * @param {Object} encryptionParams
 * @param {Base64UrlString} encryptionParams.iv initialization vector
 * @param {String} encryptionParams.mode aes encryption mode ['ccm', 'gcm', 'ocb2']
 * @param {Number} encryptionParams.ts tag size [64, 96, 128]
 * @param {String} encryptionParams.adata authenticated data to associate with the data
 * @return {ArrayBuffer} the decrypted data
 * @throws {Error} if a parameter is invalid, data is corrupt or wrong key
 * @private
 */
export function decryptFile (data, key, encryptionParams) {
  return R.compose(
    R.prop('buffer'),
    R.constructN(1, Uint8Array),
    Sjcl.codec.bytes.fromBits,
    R.curry(_decrypt)(R.__, key, encryptionParams),
    Sjcl.codec.bytes.toBits,
    R.constructN(1, Uint8Array)
  )(data)
}

/**
 * Decrypt key
 *
 * @param {Base64UrlString} data
 * @param {Base64UrlString} key
 * @param {Object} encryptionParams
 * @param {Base64UrlString} encryptionParams.iv initialization vector
 * @param {String} encryptionParams.mode aes encryption mode ['ccm', 'gcm', 'ocb2']
 * @param {Number} encryptionParams.ts tag size [64, 96, 128]
 * @param {String} encryptionParams.adata authenticated data to associate with the data
 * @return {Base64UrlString} the decrypted data
 * @throws {Error} if a parameter is invalid, data is corrupt or wrong key
 * @public
 */
export function decryptKey (data, key, encryptionParams) {
  return Sjcl.codec.base64url.fromBits(_decrypt(Sjcl.codec.base64url.toBits(data), key, encryptionParams))
}



// WEBPACK FOOTER //
// ./src/core/aes.js